import { useDispatch } from "react-redux";
import { useState, useCallback } from "react";
import { connect } from "twilio-video";
import { setErrorNotification } from "store/errors/slice";
import { removeToken } from "store/user/slice";
import { ErrorType } from "store/errors/types";
import { RoomAssignment } from "store/user/types";
import { client } from "api/client";
import { SyncClient, SyncList, SyncMap } from "twilio-sync";
import { ListItem } from "twilio-sync/lib/listitem";
import { MapItem } from "twilio-sync/lib/mapitem";
import { resetRoom } from "store/room/thunks";
import { useInterval } from "hooks/useInterval";

const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://silversunnapi-develop.azurewebsites.net";
const EXPIRATION_SECONDS = 120;
const PRUNE_EXPIRED_INTERVAL = 30000;

export function useHost(roomUid: string) {
  const dispatch = useDispatch();
  const [authenticated, setAuthenticated] = useState<boolean>();
  const [knockingList, setSyncList] = useState<SyncList>();
  const [guestMap, setSyncMap] = useState<SyncMap>();
  const [knocking, setKnocking] = useState<ListItem[]>([]);
  const [guests, setGuests] = useState<MapItem[]>();

  useInterval(async () => {
    // Periodically prune expired list items that have not pinged recently
    if (knockingList) {
      const list = await knockingList.getItems({ from: 0, order: "asc" });
      var date = new Date();
      date.setSeconds(-EXPIRATION_SECONDS);
      const expired = list.items.filter((item) => {
        return date >= (item.data as any).ping;
      });
      const removePromises = expired.map((exp) =>
        knockingList.remove(exp.index).catch((e) => {
          // Eat the "resource not found" error
          if (e.code !== 54201) {
            throw e;
          }
        })
      );
      await Promise.all(removePromises);
      setKnocking(list.items);
    }
  }, PRUNE_EXPIRED_INTERVAL);

  const startRoomAsHost = useCallback(
    async (username: string, password: string) => {
      async function updateToken(client: SyncClient, token: string) {
        const twilioSyncTokenRes = await fetch(
          API_URL + "/api/Twilio/Sync/HostToken",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: "bearer " + token,
            },
            body: JSON.stringify({
              Room: roomUid,
            }),
          }
        );

        const twilioSyncToken = await twilioSyncTokenRes.json();
        return client.updateToken(twilioSyncToken.Token);
      }

      async function updateKnocking(knockingList: SyncList) {
        const list = await knockingList.getItems({ from: 0, order: "asc" });
        setKnocking(list.items);
      }

      try {
        // Log in.
        const formData = new URLSearchParams();

        formData.append("grant_type", "password");
        formData.append("username", username);
        formData.append("password", password);
        formData.append("client_id", "SilverSunnDashboard");

        const tokenRes = await fetch(API_URL + "/Token", {
          method: "POST",
          body: formData,
        });
        const text = await tokenRes.text();
        if (!tokenRes.ok) {
          throw new Error(text);
        } else {
          setAuthenticated(true);
        }

        const ticket = JSON.parse(text);
        const access_token = ticket.access_token;

        // Get Twilio Sync host token.
        const twilioSyncTokenRes = await fetch(
          API_URL + "/api/Twilio/Sync/HostToken",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: "bearer " + access_token,
            },
            body: JSON.stringify({
              Room: roomUid,
            }),
          }
        );

        const twilioSyncToken = await twilioSyncTokenRes.json();

        if (!twilioSyncTokenRes.ok) {
          throw twilioSyncToken;
        }
        const twilioSyncClient = new SyncClient(twilioSyncToken.Token, {
          logLevel: "info",
        });

        // Tokens seem to expire after about an hour.
        // These handlers are necessary to get a new token from our API,
        // and update the client.
        twilioSyncClient.on("tokenAboutToExpire", function () {
          updateToken(twilioSyncClient, access_token);
        });
        twilioSyncClient.on("tokenExpired", function () {
          updateToken(twilioSyncClient, access_token);
        });

        /** KNOCKING LIST */
        const knockingList = await twilioSyncClient.list(roomUid + "-knocking");

        knockingList.on("itemAdded", async (_) => updateKnocking(knockingList));
        knockingList.on("itemRemoved", async (_) =>
          updateKnocking(knockingList)
        );
        knockingList.on("itemUpdated", async (_) =>
          updateKnocking(knockingList)
        );

        const list = await knockingList.getItems({ from: 0, order: "asc" });
        setKnocking(list.items);
        setSyncList(knockingList);

        /** GUEST MAP */
        const guestMap = await twilioSyncClient.map(roomUid + "-guests");
        // A guest is removed remotely when a user disconnects/leaves the page.
        guestMap.on("itemRemoved", async (_) => {
          const map = await guestMap.getItems();
          setGuests(map.items);
        });

        const guests = await guestMap.getItems();
        setGuests(guests.items);
        setSyncMap(guestMap);

        /** TWILIO VIDEO ROOM */
        const { Token } = await client.twilio_PostToken({
          Room: roomUid,
          Identity: "HostFromBrowser",
        });

        if (Token) {
          const newRoom = await connect(Token, {
            name: roomUid,
            tracks: [],
            bandwidthProfile: {
              video: {
                mode: "grid",
                dominantSpeakerPriority: "standard",
                renderDimensions: {
                  high: { height: 720, width: 1280 },
                  standard: { height: 720, width: 1280 },
                  low: { height: 480, width: 858 },
                },
              },
            },
            dominantSpeaker: true,
            networkQuality: { local: 1, remote: 0 },
          });

          newRoom.once("disconnected", (_, err) => {
            if (err) {
              console.error(err);
              dispatch(
                setErrorNotification({
                  name: err.code,
                  type: ErrorType.DisconnectionError,
                  message: err.message,
                })
              );
            }
          });
          window.addEventListener("beforeunload", () => newRoom.disconnect());
          window.addEventListener("pagehide", () => newRoom.disconnect());
        }
      } catch (err) {
        if ("code" in err) {
          console.error(err);
          dispatch(
            setErrorNotification({
              name: err.code,
              type: ErrorType.ConnectionError,
              message: err.message,
            })
          );
        } else {
          console.error(err);
          dispatch(
            setErrorNotification({
              name: err.name,
              type: ErrorType.MediaError,
              message: err.message,
            })
          );
        }
        dispatch(removeToken());
        dispatch(resetRoom());
      }
    },
    [dispatch, roomUid]
  );

  const update = useCallback(
    async (idx: number, data: { id: string; name: string; status: string }) => {
      try {
        await knockingList?.update(idx, {
          status: data.status,
          lastUpdatedBy: "host",
        });
        const list = await knockingList?.getItems({ from: 0, order: "asc" });
        if (list?.items.length) {
          setKnocking(list.items);
        } else {
          setKnocking([]);
        }

        // User admitted
        if (data.status === "admitted") {
          await guestMap?.set(data.name, {
            name: data.name,
            area: RoomAssignment.LiveBroadcast,
          });
        }

        // User excluded (moved back to waiting)
        if (data.status === "waiting") {
          await guestMap?.remove(data.name);
        }

        // User kicked
        if (data.status === "rejected") {
          await guestMap?.remove(data.name);
        }
      } catch (e) {
        console.error(e);
      } finally {
        const guests = await guestMap?.getItems();
        setGuests(guests?.items);
      }
    },
    [knockingList, guestMap]
  );

  const move = useCallback(
    async (id: string, roomAssignment: RoomAssignment) => {
      await guestMap?.update(id, {
        area: roomAssignment,
      });
      const guests = await guestMap?.getItems();
      setGuests(guests?.items);
    },
    [guestMap]
  );

  return { authenticated, startRoomAsHost, knocking, guests, update, move };
}
